import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { usePlayerAuth } from "../context/PlayerAuthContext";
import { toast } from "sonner";
import { ACADEMY_LOGO_H } from "../data/assets";
import { Ticket, Crown, Loader2 } from "lucide-react";

// Parent-facing "Redeem Team Code" flow -> instant 7-day Elite trial, joined to the coach's team.
export default function RedeemDialog({ open, onClose, onSuccess, initialCode = "" }) {
  const { redeem } = usePlayerAuth();
  const [code, setCode] = useState(initialCode);
  const [displayName, setDisplayName] = useState("");
  const [jersey, setJersey] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setCode(initialCode); }, [open, initialCode]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const player = await redeem({
        code: code.trim().toUpperCase(),
        email: email.trim(),
        password,
        name: name.trim(),
        display_name: displayName.trim(),
        jersey_number: jersey.trim(),
      });
      toast.success(`You're in! 7-day Elite trial unlocked for ${player.display_name || player.name || "your player"}.`);
      onClose();
      onSuccess && onSuccess(player);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't redeem that code. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const field = "w-full bg-[#0d0d0d] border border-white/10 rounded-md p-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md bg-[#111] border border-[#D4AF37]/40 text-white max-h-[90vh] overflow-y-auto" data-testid="redeem-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <img src={ACADEMY_LOGO_H} alt="" className="h-10 w-auto object-contain" />
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.2em] text-[#D4AF37] bg-[#D4AF37]/12 border border-[#D4AF37]/40 rounded-full px-2.5 py-1"><Crown size={12} /> 7-Day Elite Trial</span>
          </div>
          <DialogTitle className="font-headings text-2xl uppercase tracking-wide">Redeem Team Code</DialogTitle>
          <DialogDescription className="text-white/50 text-sm">Enter the code from your coach to unlock a free 7-day Elite trial and join your team's leaderboard.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="relative">
            <Ticket size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#D4AF37]" />
            <input data-testid="redeem-code" required value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Team code (e.g. U9BOYS-7K4Q)"
              className={field.replace("p-3", "py-3 pl-9 pr-3") + " font-mono uppercase tracking-wider"} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="redeem-display-name" required value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Player name" className={field} />
            <input data-testid="redeem-jersey" value={jersey} onChange={(e) => setJersey(e.target.value)} placeholder="Jersey # (optional)" className={field} />
          </div>
          <input data-testid="redeem-parent-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Parent / account name (optional)" className={field} />
          <input data-testid="redeem-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className={field} />
          <input data-testid="redeem-password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Create a password (min 6 chars)" className={field} />
          <button data-testid="redeem-submit" type="submit" disabled={busy}
            className="w-full flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#e8c65a] text-black font-subheadings font-bold uppercase tracking-wider py-3 rounded-sm transition-colors disabled:opacity-60">
            {busy ? <><Loader2 size={16} className="animate-spin" /> Redeeming…</> : "Start My Free Trial"}
          </button>
        </form>
        <p className="text-xs text-white/45 mt-1">Already have an account with this email? Log in first, then redeem — or enter your existing password above.</p>
      </DialogContent>
    </Dialog>
  );
}
